import { atom, useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import type { CarbonPrice } from '../../graphql';
import { Currency, useCarbonPriceMutation } from '../../graphql';
import {
  convertMonetaryValue,
  useCurrencyRates,
  useDefaultCurrency,
} from '../../state/zustand';

export const carbonPriceAtom = atom<Pick<
  CarbonPrice,
  'price' | 'updatedAt' | 'isOverride'
> | null>(null);

export function parseCarbonPriceData(
  carbonPrice: CarbonPrice | null | undefined,
) {
  if (!carbonPrice) {
    return null;
  }
  const price = +(carbonPrice.price ?? NaN);
  if (price && Number.isFinite(price)) {
    return {
      price,
      updatedAt: carbonPrice.updatedAt,
      isOverride: carbonPrice.isOverride ?? false,
    };
  }
  return null;
}

export function useCarbonPrice(opts?: { skip?: boolean; refetch?: boolean }) {
  const [carbonPriceData, setCarbonPriceData] = useAtom(carbonPriceAtom);
  const [carbonPriceMutation] = useCarbonPriceMutation();
  const defaultCurrency = useDefaultCurrency();
  const currencyRates = useCurrencyRates();

  useEffect(() => {
    if (opts?.skip || (carbonPriceData !== null && !opts?.refetch)) return;

    carbonPriceMutation().then(async (result) => {
      const priceData = parseCarbonPriceData(result.data?.carbonPrice);

      if (priceData) {
        setCarbonPriceData(priceData);
      }
    });
  }, [
    carbonPriceData,
    carbonPriceMutation,
    opts?.skip,
    opts?.refetch,
    setCarbonPriceData,
  ]);

  // Memoize the return value to maintain stable object reference
  return useMemo(() => {
    const isOverride = carbonPriceData?.isOverride ?? false;
    // NOTE: Currency for the external API price is currently assumed to be EUR
    const currency =
      carbonPriceData?.price && !isOverride ? Currency.EUR : defaultCurrency;

    const convertedPrice = carbonPriceData?.price
      ? convertMonetaryValue(carbonPriceData.price, currency, currencyRates)
      : null;

    return {
      carbonPrice: convertedPrice,
      updatedAt: carbonPriceData?.updatedAt ?? null,
      isOverride: carbonPriceData?.isOverride ?? false,
    };
  }, [
    carbonPriceData?.price,
    carbonPriceData?.updatedAt,
    carbonPriceData?.isOverride,
    currencyRates,
    defaultCurrency,
  ]);
}
